import React, { useContext } from 'react';
import Menu, { SubMenu, Item as MenuItem } from 'rc-menu';
import { ContextMenuRenderProps } from './types';
import { defaultOnCopy } from './utils/copy';
import { generateUniqKey } from './utils';
import { GridStore } from './store/gridStore';
import { ContextMenuStore } from './store/contextMenu';
import { RowStore } from './store/rowStore';
import { formatMessage } from './locales';
import { isNil } from 'lodash';
import { css } from '@linaria/core';

const cellMenuClassname = css`
  background-color: var(--background-primary-color);
  list-style-type: none;
  min-width: 140px;
  padding: 4px;
  margin: 0;
  border-radius: 4px;
  box-shadow:
    0 6px 16px 0 rgba(0, 0, 0, 0.08),
    0 3px 6px -4px rgba(0, 0, 0, 0.12),
    0 9px 28px 8px rgba(0, 0, 0, 0.05);
  .rc-menu-item,
  .rc-menu-submenu {
    line-height: 20px;
    padding: 5px 12px;
    &:hover {
      background-color: var(--hover-color);
    }
  }
  .rc-menu-item {
    cursor: pointer;
  }
`;
const cellSubMenuClassname = css`
  position: absolute;
  padding-left: 20px;
  &.rc-menu-submenu-hidden {
    display: none;
  }
  .rc-menu {
    background-color: var(--background-primary-color);
    list-style-type: none;
    padding: 4px;
    min-width: 140px;
    margin: 0;
    border-radius: 4px;
    box-shadow:
      0 6px 16px 0 rgba(0, 0, 0, 0.08),
      0 3px 6px -4px rgba(0, 0, 0, 0.12),
      0 9px 28px 8px rgba(0, 0, 0, 0.05);
    .rc-menu-item,
    .rc-menu-submenu {
      line-height: 20px;
      padding: 5px 12px;
      &:hover {
        background-color: var(--hover-color);
      }
    }
    .rc-menu-item {
      cursor: pointer;
    }
  }
`;

enum MenuCommands {
  odc_inner_copy = 'odc_inner_copy',
  odc_inner_add = 'odc_inner_add',
  odc_inner_delete = 'odc_inner_delete',
  odc_inner_up = 'odc_inner_up',
  odc_inner_down = 'odc_inner_down',
  odc_inner_frozen = 'odc_inner_frozen',
  odc_inner_unfreeze_all = 'odc_inner_unfreeze_all',
  odc_inner_copy_column = 'odc_inner_copy_column',
}

const DefaultContextMenuRender = <R,>(props: ContextMenuRenderProps<R>) => {
  const {
    setContextMenuVisible,
    config,
    isRowSelected,
    isColumnSelected,
    rowIdx,
    row,
    rowKeyName,
  } = props;
  const rowStore = useContext(RowStore);
  const gridStore = useContext(GridStore);
  const contextMenuStore = useContext(ContextMenuStore);
  const {
    onAddRows,
    onDeleteRow,
    onRowReorder,
    enableAddAction,
    enableRowRecord,
    enableRowTools,
    readonly,
  } = contextMenuStore;
  const isShowTools = enableRowTools && !readonly && isRowSelected;
  const rows = gridStore?.gridRef?.rows;
  const rowsCount = rows?.length ?? 0;
  const isSelectMultiRow = (gridStore.gridRef?.selectedRows?.size ?? 0) > 1;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rowKey = gridStore?.gridRef?.rowKeyGetter?.(row as { [key: string]: any });
  const visibleConfig = !isRowSelected ? config : config?.filter((item) => item?.isShowRowSelected);
  return (
    <Menu
      data-role="cellMenu"
      onClick={(e) => {
        e.domEvent.stopPropagation();
        switch (e.key) {
          case MenuCommands.odc_inner_copy: {
            if (gridStore.gridRef) {
              defaultOnCopy(gridStore.gridRef);
            }
            break;
          }
          case MenuCommands.odc_inner_copy_column: {
            if (gridStore.gridRef) {
              defaultOnCopy(gridStore.gridRef);
            }
            break;
          }
          case MenuCommands.odc_inner_add: {
            onAddRows?.([
              {
                ...row,
                [rowKeyName]: generateUniqKey(),
              },
            ]);
            break;
          }
          case MenuCommands.odc_inner_delete: {
            onDeleteRow?.(row);
            break;
          }
          case MenuCommands.odc_inner_up: {
            onRowReorder?.(rowIdx, rowIdx - 1);
            break;
          }
          case MenuCommands.odc_inner_down: {
            onRowReorder?.(rowIdx, rowIdx + 1);
            break;
          }
          case MenuCommands.odc_inner_frozen: {
            const selectRows = gridStore.gridRef?.selectedRows;
            if (selectRows?.size) {
              rowStore.changeTopRowSet(new Set([...selectRows, ...rowStore.topRowSet]));
            }
            break;
          }
          case MenuCommands.odc_inner_unfreeze_all: {
            rowStore.changeTopRowSet(new Set());
            break;
          }
          default: {
            return;
          }
        }
        setContextMenuVisible(false);
      }}
      getPopupContainer={(triggerNode) => triggerNode.closest('.rdg') ?? document.body}
      className={cellMenuClassname}
    >
      {isRowSelected ? (
        <MenuItem key={MenuCommands.odc_inner_copy}>
          {formatMessage('DefaultContextMenuRender.copyRow')}
        </MenuItem>
      ) : null}
      {isRowSelected && gridStore.gridRef?.enableFrozenRow
        ? [
            !isNil(rowKey) && !rowStore.topRowSet.has(rowKey) ? (
              <Menu.Item key={MenuCommands.odc_inner_frozen}>
                {formatMessage('DefaultContextMenuRender.frozenRow')}
              </Menu.Item>
            ) : null,
            <Menu.Item key={MenuCommands.odc_inner_unfreeze_all}>
              {formatMessage('DefaultContextMenuRender.unFrozenAll')}
            </Menu.Item>,
          ].filter(Boolean)
        : null}
      {isColumnSelected && <Menu.Item key={MenuCommands.odc_inner_copy_column}>复制列</Menu.Item>}
      {isShowTools
        ? [
            onAddRows && enableAddAction && !isSelectMultiRow ? (
              <Menu.Item key={MenuCommands.odc_inner_add}>
                {formatMessage('DefaultContextMenuRender.newRow')}
              </Menu.Item>
            ) : null,
            onDeleteRow && enableAddAction && !isSelectMultiRow ? (
              <Menu.Item key={MenuCommands.odc_inner_delete}>
                {formatMessage('DefaultContextMenuRender.deleteRow')}
              </Menu.Item>
            ) : null,
            onRowReorder && enableRowRecord && rowIdx > 0 && !isSelectMultiRow ? (
              <Menu.Item key={MenuCommands.odc_inner_up}>
                {formatMessage('DefaultContextMenuRender.upRow')}
              </Menu.Item>
            ) : null,
            onRowReorder && enableRowRecord && rowIdx !== rowsCount - 1 && !isSelectMultiRow ? (
              <Menu.Item key={MenuCommands.odc_inner_down}>
                {formatMessage('DefaultContextMenuRender.downRow')}
              </Menu.Item>
            ) : null,
          ].filter(Boolean)
        : null}
      {visibleConfig?.map((c) => {
        if (c.children) {
          return (
            <SubMenu popupClassName={cellSubMenuClassname} key={c.key} title={c.text}>
              {c.children.map((child) => {
                return (
                  <MenuItem
                    disabled={child.disabled}
                    onClick={() => {
                      child.onClick?.(row);
                      setContextMenuVisible(false);
                    }}
                    key={child.key}
                  >
                    {child.text}
                  </MenuItem>
                );
              })}
            </SubMenu>
          );
        }
        return (
          <Menu.Item
            disabled={c.disabled}
            key={c.key}
            onClick={() => {
              c.onClick?.(row);
              setContextMenuVisible(false);
            }}
          >
            {c.text}
          </Menu.Item>
        );
      })}
    </Menu>
  );
};
export default DefaultContextMenuRender;
